/**
 * A custom tooltip component.
 * Replaces MUI Tooltip component.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Element that triggers tooltip
 * @param {string} props.title - Tooltip text
 * @param {string} [props.placement="top"] - Tooltip position (top, bottom, left, right)
 * @param {number} [props.delay=200] - Delay before showing tooltip in milliseconds
 * @returns {JSX.Element} A wrapper with tooltip functionality
 *
 * @author Forge
 * @date 2026-04-03
 * @version 1.0.0
 */


import React, { useState, useRef, useEffect } from "react";

export default function CustomTooltip({ children, title, placement = "top", delay = 200 }) {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    const id = setTimeout(() => setIsVisible(true), delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) clearTimeout(timeoutId);
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  // Get position style based on placement
  const getTooltipStyle = () => {
    if (!containerRef.current) return {};
    
    const rect = containerRef.current.getBoundingClientRect();
    const tooltipHeight = 40; // Approximate height
    const tooltipWidth = 120; // Approximate width
    
    switch (placement) {
      case 'top':
        return {
          bottom: `calc(100% + 8px)`,
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          top: `calc(100% + 8px)`,
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          right: `calc(100% + 8px)`,
          top: '50%',
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          left: `calc(100% + 8px)`,
          top: '50%',
          transform: 'translateY(-50%)',
        };
      default:
        return {};
    }
  };

  return (
    <div
      ref={containerRef}
      className="custom-tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`custom-tooltip custom-tooltip-${placement}`}
          style={getTooltipStyle()}
        >
          {title}
          <span className="custom-tooltip-arrow"></span>
        </div>
      )}
    </div>
  );
}
